import { Company } from '@backend/domain'
import { CompanyRepository, ReviewsRepository } from '@backend/repository'
import {
	FeedMetadata_ProcessingInstruction,
	SchedulingRules_AdmissionPolicy,
	Service,
	Service_ServiceType,
	ServiceFeed,
} from '@core/google-reservations/types/feeds'
import { Processor, WorkerHost } from '@nestjs/bullmq'
import { Logger } from '@nestjs/common'
import { getUnixTime } from 'date-fns'
import SFTPClient from 'ssh2-sftp-client'
import { In } from 'typeorm'
import { TracedProcessor } from '../../../../../../common'
import { GOOGLE, Stage } from '../../../../../../config'
import { GOOGLE_RESERVATIONS_QUEUES, UploadServicesFeedJob } from '../../../../../../core'
import { DEFAULT_SLOT_LENGTH } from '../../constants'
import { getServiceId } from '../../utils'

@Processor(GOOGLE_RESERVATIONS_QUEUES.UPLOAD_SERVICES_FEED)
export class UploadServicesFeedProcessor extends WorkerHost {
	private readonly logger = new Logger(UploadServicesFeedProcessor.name)

	private readonly sftpClient: SFTPClient

	private restaurants: Company[] = []

	constructor(
		private readonly _companyRepository: CompanyRepository,
		private readonly _reviewsRepository: ReviewsRepository,
	) {
		super()

		this.sftpClient = new SFTPClient()
	}

	@TracedProcessor()
	async process(job: UploadServicesFeedJob): Promise<void> {
		const { restaurantIds } = job.data

		await this.uploadFeed(restaurantIds)
	}

	private async uploadFeed(restaurantIds: string[]): Promise<void> {
		try {
			await this.loadRestaurantData(restaurantIds)

			const feed = await this.generateFeed()

			await this.sftpClient.connect({
				host: GOOGLE.ACTION_CENTER.FEEDS.URL,
				port: GOOGLE.ACTION_CENTER.FEEDS.PORT,
				privateKey: GOOGLE.ACTION_CENTER.FEEDS.SFTP_KEY,
				username: GOOGLE.ACTION_CENTER.FEEDS.USERNAMES.SERVICES,
			})

			await this.sftpClient.put(
				Buffer.from(JSON.stringify(feed)),
				`Service_Feed_${Stage}_${feed.metadata?.generation_timestamp}.json`,
			)

			this.logger.log({ message: 'Service feed uploaded', recordCount: feed.service?.length })
		} catch (error) {
			this.logger.error({ message: 'Failed to upload Service feed', error })
			throw error
		} finally {
			await this.sftpClient.end()
		}
	}

	private async generateFeed(): Promise<ServiceFeed> {
		const restaurantIds = this.restaurants.map((restaurant) => restaurant.id)

		const reviews = await this._reviewsRepository.find({
			where: { tenantId: In(restaurantIds) },
		})
		const reviewsMap = new Map(reviews.map((review) => [review.tenantId as string, review]))

		const services: Service[] = []

		for (const restaurant of this.restaurants) {
			const reviews = reviewsMap.get(restaurant.id)

			const service: Service = {
				merchant_id: restaurant.id,
				service_id: getServiceId(restaurant.id),
				localized_service_name: {
					value: 'Dining Reservation',
					localized_value: [
						{ locale: 'en-US', value: 'Dining Reservation' },
						{ locale: 'de-DE', value: 'Essreservierung' },
					],
				},
				rules: {
					min_advance_booking: (DEFAULT_SLOT_LENGTH * 60).toString(),
					admission_policy: SchedulingRules_AdmissionPolicy.TIME_FLEXIBLE,
				},
				type: Service_ServiceType.SERVICE_TYPE_DINING_RESERVATION,
				rating: { number_of_ratings: reviews?.totalRatings.toString(), value: reviews?.rating },
			}

			services.push(service)
		}

		const feed: ServiceFeed = {
			metadata: {
				processing_instruction: FeedMetadata_ProcessingInstruction.PROCESS_AS_COMPLETE,
				total_shards: 1,
				shard_number: 0,
				generation_timestamp: getUnixTime(new Date()).toString(),
			},
			service: services,
		}
		return feed
	}

	private async loadRestaurantData(restaurantIds: string[]): Promise<void> {
		this.restaurants = await this._companyRepository.find({
			where: { id: In(restaurantIds) },
		})
	}
}
